import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useApi } from '../../../lib/api';
import { ApiProblemError, fetchJson } from '../../../lib/apiFetchJson';
import { logger } from '../../../lib/logger';
import {
  FlowViewStatePutBodySchema,
  FlowViewStateResponseSchema,
  type FlowViewStatePutBody,
  type FlowViewStateResponse,
  type FlowWorkingState,
} from '@shared/flow/view-state';
import { useDebouncedValue } from '../useDebouncedValue';
import type { FlowViewEntity } from './useViews';
import { safeParseJson, summarizeIssues } from '@shared/parse-json';
import { backgroundWrite } from '../../../lib/background-write';

/**
 * Working-state persistence — the client half of plan U9 (R8).
 *
 * This is the hook pair that kills Rob Cook's complaint ("every time I toggle
 * between Accounts and Contacts my filters reset", 2026-08-11): the rep's
 * active view, their unsaved modifications, and their default landing view
 * live in ONE server row per `(user, entity)`, so they survive a tab switch,
 * a refresh, a new tab, and a second machine.
 *
 *  - `useFlowViewState(entity)` — the LOAD half. GETs the row once per mount,
 *    reports `isRestored` the moment the request settles (success OR failure),
 *    and hands the payload to `useViewStateMachine`, which owns the restore
 *    precedence (`?view=` → stored active → default → code preset).
 *  - `useFlowViewStatePersist({…})` — the WRITE half. Debounced ~1s trailing
 *    edge over `{activeViewId} + FlowWorkingStateSchema`, so a rep dragging a
 *    slider or retyping a filter value produces one PUT, not thirty.
 *
 * ── Degradation contract (both halves fail SOFT) ────────────────────────────
 * The store is a convenience, never a dependency. A GET 500 resolves
 * `isRestored: true` with `restored: null`, so the table loads on its
 * code-preset defaults instead of hanging behind a gate; a PUT 400/500 is
 * logged and dropped, leaving the rep's local state exactly as it was (their
 * filters keep working; only the durability is lost). Neither path throws.
 * Both halves set `meta.suppressErrorToast` — a failure here must never
 * surface as a toast (mutation-cache-handler.ts) — and log a
 * `[flow-view-state] load/save failed` warning instead, so a recurring
 * failure is still visible in logs.
 *
 * ── Serialization shape ─────────────────────────────────────────────────────
 * `FlowWorkingStateSchema` (src/shared/flow/view-state.ts) is BOTH the
 * server's `parseDbJson` schema for `flow_view_state.working_state` and this
 * client's serialization shape — one schema, no parallel hand-rolled type.
 * `search` is deliberately absent from it (doc's dirty-state table), so a
 * free-text search is never persisted and never restores.
 *
 * ── `stateKey` vs. `FlowViewEntity` (QA-2 / V3) ─────────────────────────────
 * These two hooks key the `(user, X)` state row by a `stateKey`, which is
 * DELIBERATELY a wider type than `FlowViewEntity`. `FlowViewEntity`
 * ('contact' | 'account') means "which saved-views catalog" — it drives
 * `GET /api/flow/views?entity=` and never changes for a given table.
 * `stateKey` means "which working-state ROW" — it can diverge from that when
 * a mount must not share the Command Center table's row. The account-scoped
 * `ContactFlowTable` (drill-down / main-Terra Contact tab) is exactly that
 * case: it passes `stateKey: 'contact_account'` (one row for EVERY
 * drill-down, not per-account — a per-account row would multiply
 * unboundedly) while still passing `entity: 'contact'` to
 * `useViewStateMachine` for the saved-views catalog. See
 * `src/api/flow-view-state.ts`'s module doc for the server half. The Plays
 * mount (`ContactFlowTable` with `mount: 'play'`) is the same shape again:
 * `stateKey: 'contact_play'` gives it its own single working-state row for
 * the WHOLE Plays mount (not per-play — same unbounded-row reasoning as
 * `contact_account`), mirroring how `contact_account` covers every
 * drill-down.
 *
 * `'plays'` (plan 2026-09-10-001 A2/U9) is the odd one out: it is the only
 * state key with NO saved-views catalog behind it at all. It holds the PLAYS
 * LIST's own working filters — the chips `PlaysFilterStrip` renders above the
 * accordion — so the list restores them after a reload exactly as the Contact
 * component restores its own. There is no `FlowViewEntity` 'plays' and no
 * `useViewStateMachine` instance for it (a saved-view entity for the plays
 * list is explicitly out of scope); `usePlaysWorkingFilters`
 * (`../plays/PlaysFilterStrip.tsx`) drives the two hooks below directly.
 *
 * `'contact_play_candidates'` (plan 2026-09-24-005 U14, D1): the Plays tab
 * "Not in this play yet" section — an unbound table with working state
 * only, one row per user, shared across every play. The literal is
 * `CONTACT_PLAY_CANDIDATES_STATE_KEY` (`@shared/flow/play-people`). Must
 * equal the server's `FLOW_VIEW_STATE_ENTITIES` (`src/shared/flow/view-state.ts`) —
 * pinned on both sides by tests.
 *
 * `'contact_play_responded'` / `'contact_play_not_responded'` (plan
 * 2026-09-25-002 U3/D4): the two `ContactFlowTable`-backed member sections
 * on the contacts route, each driven through `useViewStateMachine` (a real
 * saved-views machine) with its own namespaced `viewParam`
 * (`` `view_${stateKey}` ``, D3) so the three Plays-tab mounts never observe
 * each other's `?view=`. The literals are the values of
 * `CONTACT_PLAY_SECTION_STATE_KEYS` (`@shared/flow/play-people`) — they
 * replaced the members route's own two per-section working-state rows,
 * retired by plan U9.
 */

export type FlowViewStateKey =
  | FlowViewEntity
  | 'contact_account'
  | 'contact_play'
  | 'plays'
  | 'contact_play_candidates'
  | 'contact_play_responded'
  | 'contact_play_not_responded';

/** Root of the query key; the state key is appended. */
export const FLOW_VIEW_STATE_QUERY_KEY = ['flow-view-state'] as const;

export function flowViewStateQueryKey(stateKey: FlowViewStateKey) {
  return [...FLOW_VIEW_STATE_QUERY_KEY, stateKey] as const;
}

/** Trailing-edge debounce for the persist PUT (plan U9: "~1s"). */
export const VIEW_STATE_PERSIST_DEBOUNCE_MS = 1000;

/**
 * F-C — one PUT lane per `(user, entity)` row, shared across EVERY
 * `useFlowViewStatePersist` instance for that entity, not per hook instance.
 *
 * The single-instance serialization below (F15: at most one PUT in flight,
 * later edits coalesce into one pending slot) is correct for one mounted
 * component, but a Command Center tab switch unmounts the table without
 * waiting for an in-flight PUT to finish (see the unmount-flush doc comment
 * — that's deliberate, the request outlives the component). Switching back
 * mounts a BRAND NEW hook instance. If the lane lived on that instance's own
 * refs, the new instance would have no idea the old one still had a request
 * in flight: it could fire its own PUT concurrently, and whichever of the
 * two the network settles LAST wins — the exact lost-update bug F15 closed,
 * reopened one layer up by the remount. Keying the lane by entity means the
 * new instance joins the SAME lane the old one was using, so the old
 * request's completion drains whatever is newest in the shared pending slot
 * — even if that state was queued by the NEW instance — instead of racing it.
 *
 * Module-scope state, not a ref: this is intentionally NOT reset by React's
 * mount/unmount lifecycle (StrictMode's dev setup→cleanup→setup included —
 * see the mount effect below, which resets only per-instance refs). A lane
 * describes an in-flight request against the SERVER row, which by definition
 * outlives any one component instance.
 */
interface ViewStatePersistLane {
  /** True from the moment a PUT is issued (by ANY instance sharing this
   *  lane) until its promise settles. */
  inFlight: boolean;
  /** The single coalescing slot: the latest payload waiting for the lane,
   *  from whichever instance produced it last. */
  pending: string | null;
}

const persistLanesByStateKey = new Map<FlowViewStateKey, ViewStatePersistLane>();

function getPersistLane(stateKey: FlowViewStateKey): ViewStatePersistLane {
  let lane = persistLanesByStateKey.get(stateKey);
  if (lane === undefined) {
    lane = { inFlight: false, pending: null };
    persistLanesByStateKey.set(stateKey, lane);
  }
  return lane;
}

export interface FlowViewStateStore {
  /**
   * The mount's fetch gate: true once the GET has SETTLED, success or
   * failure. Mounts fold this into the state machine's existing `ready` flag
   * (`ready: identityReady && viewState.isRestored`) rather than stacking a
   * second gate — see ContactFlowTable/AccountFlowTable.
   */
  isRestored: boolean;
  /** The stored row, or null when nothing is stored / the GET failed / disabled. */
  restored: FlowViewStateResponse | null;
  /** The user's landing view for this entity (preset slug or saved-view id). */
  defaultViewId: string | null;
  /**
   * "Set as default" — writes ONLY the pointer, leaving working state alone.
   * Null on a disabled store: a mount that doesn't own this `(user, entity)`
   * row (the account-scoped contact roster) must not be able to repoint the
   * Command Center table's landing view either, so the affordance is absent
   * rather than silently no-op.
   */
  setDefaultView: ((viewId: string) => Promise<void>) | null;
}

export interface UseFlowViewStateOptions {
  /**
   * Off for a mount that must not own ANY per-user state row at all (there is
   * currently no such mount — the account-scoped `ContactFlowTable` got its
   * OWN row, `stateKey: 'contact_account'`, in QA-2/V3, rather than being
   * disabled). Kept for any future mount that genuinely has nowhere to
   * persist to. Disabled reports `isRestored: true` immediately so it can
   * never gate a fetch.
   */
  enabled?: boolean;
}

export function useFlowViewState(
  stateKey: FlowViewStateKey,
  options: UseFlowViewStateOptions = {},
): FlowViewStateStore {
  const { enabled = true } = options;
  const { apiFetch } = useApi();
  const queryClient = useQueryClient();

  const query = useQuery<FlowViewStateResponse>({
    queryKey: flowViewStateQueryKey(stateKey),
    enabled,
    staleTime: Infinity,
    // One shot. A retry ladder would hold the table's first fetch behind a
    // gate for seconds to recover a convenience; degrading to code presets
    // immediately is the better trade (plan U9: "degrade, don't block").
    retry: false,
    // Degradation contract (module doc): fail SOFT — the table proceeds on
    // code presets, so a load failure must never surface as a toast.
    meta: { suppressErrorToast: true },
    queryFn: async () => {
      try {
        return await fetchJson(apiFetch, FlowViewStateResponseSchema, `/api/flow/view-state/${stateKey}`);
      } catch (err) {
        // The suppressed global handler (mutation-cache-handler.ts) returns
        // before logging anything — log here so a recurring failure is still
        // visible, then rethrow so react-query still resolves `isError`.
        logger.warn({ err, stateKey }, '[flow-view-state] load failed');
        throw err;
      }
    },
  });

  const putMutation = useMutation({
    // Degradation contract (module doc): a PUT failure is logged and
    // dropped, never surfaced as a toast — see the queryFn comment above.
    meta: { suppressErrorToast: true },
    mutationFn: async (body: FlowViewStatePutBody): Promise<FlowViewStateResponse> => {
      try {
        return await fetchJson(apiFetch, FlowViewStateResponseSchema, `/api/flow/view-state/${stateKey}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        });
      } catch (err) {
        logger.warn({ err, stateKey }, '[flow-view-state] save failed');
        throw err;
      }
    },
  });

  const { mutateAsync } = putMutation;
  const setDefaultView = useCallback(async (viewId: string): Promise<void> => {
    try {
      const row = await mutateAsync({ defaultViewId: viewId });
      // The response IS the fresh row — seed the cache with it rather than
      // invalidating (a refetch would re-read exactly what we already hold,
      // and the project rule is cache-indefinitely + update-on-mutation).
      queryClient.setQueryData(flowViewStateQueryKey(stateKey), row);
    } catch (err) {
      // A 2xx answer this bundle cannot parse (version skew only) means the
      // server DID commit the pointer: patch the cached row instead of
      // leaving the old default on screen.
      if (err instanceof ApiProblemError && err.kind === 'malformed') {
        queryClient.setQueryData<FlowViewStateResponse>(
          flowViewStateQueryKey(stateKey),
          (prev) => prev && { ...prev, defaultViewId: viewId },
        );
      }
      logger.warn({ err, stateKey, viewId }, '[flow-view-state] set-default failed');
    }
  }, [mutateAsync, queryClient, stateKey]);

  // `isError` counts as restored: the mount must proceed on code presets. A
  // malformed 2xx GET counts the same as a 5xx (skew-only trigger).
  const settled = query.isSuccess || query.isError;
  const restored = query.data ?? null;

  return useMemo(() => ({
    isRestored: enabled ? settled : true,
    restored: enabled ? restored : null,
    defaultViewId: (enabled ? restored?.defaultViewId : null) ?? null,
    setDefaultView: enabled ? setDefaultView : null,
  }), [enabled, settled, restored, setDefaultView]);
}

export interface UseFlowViewStatePersistOptions {
  stateKey: FlowViewStateKey;
  /**
   * Gate. Mounts pass `machine.isReady` — writing before the machine has
   * resolved would persist the pre-restore seed state over the rep's real
   * state.
   */
  enabled: boolean;
  activeViewId: string;
  working: FlowWorkingState;
  /**
   * A24 — "the first payload you see is NOT what the server holds."
   *
   * Default (`false`): the first payload seen after `enabled` flips is the
   * RESTORED one, so it is recorded as already-persisted and never echoed
   * back. That is what makes a plain page load issue zero writes, and it is
   * the right default for every caller that opens the gate only once the
   * restored row has been applied verbatim.
   *
   * `true`: the caller MERGED something into that first payload that the
   * server does not have — an edit the rep made while the GET was still in
   * flight, which a per-slice touch guard kept in state instead of letting
   * the restore overwrite (the Plays list's former hand-rolled working-state
   * hook did this; it moved onto the saved-views machine in plan
   * 2026-11-03-001 U6, so no caller sets this today). Recording that merged
   * payload as the baseline is how the edit was silently lost: the server
   * still held the old row, nothing further changed, and neither the debounce
   * nor the unmount flush ever had a diff to send, so reloading restored the
   * stale row (Codex round-3 #3). With this set, the baseline stays `null`,
   * so the merged payload is written ONCE on the first debounce tick.
   *
   * Read only on the render the gate opens; flipping it later does nothing.
   */
  initialPayloadIsDirty?: boolean;
  /** Overridable only so tests can shrink the window. */
  debounceMs?: number;
}

/**
 * Writes `{activeViewId, workingState}` back to the server on the trailing
 * edge of a ~1s debounce, and only when the payload actually changed.
 *
 * The first payload seen after `enabled` flips is the RESTORED one (the
 * machine seeds from it), so it is recorded as already-persisted and never
 * echoed back — a plain page load issues zero writes. Pass
 * `initialPayloadIsDirty` when that assumption does not hold because the
 * caller merged a pre-restore edit into it (A24).
 *
 * ── Unmount flush (F8) ──────────────────────────────────────────────────────
 * A Command Center tab switch UNMOUNTS the table, which clears the debounce
 * timer. Without a flush, every edit made in the last ~1s before the switch
 * died with the mount — Rob's complaint one layer down. The cleanup therefore
 * sends the newest payload seen WHILE THE GATE WAS OPEN, if it hasn't been
 * sent already.
 *
 * Deliberately NOT flushed: anything produced while the gate was CLOSED (so
 * `latestEnabledPayloadRef` still holds the last state from an enabled mount).
 * Writing an `activeViewId` a later mount cannot resolve would destroy the
 * pointer to the real view the rep was on — the machine's first resolution
 * drops a stored active id it cannot resolve — landing them on the default
 * preset.
 *
 * ── One writer at a time (F15) ──────────────────────────────────────────────
 * Concurrent PUTs to the same row are a lost-update bug: a stalled older body
 * can settle last and become the stored row. At most ONE PUT is in flight;
 * anything produced behind it is coalesced into a single pending slot holding
 * the LATEST payload, which goes out when the lane frees. Intermediates are
 * dropped (they're stale by then) and a failed body is never retried — the
 * degradation contract above still holds.
 *
 * ── The lane survives a remount (F-C) ───────────────────────────────────────
 * That lane is keyed by STATE KEY at module scope (`getPersistLane`, above)
 * — not held in this instance's own refs — so a Command Center tab switch
 * (unmount the table, then remount it) joins the SAME lane an in-flight
 * request from the old instance is still occupying, instead of starting a
 * second, uncoordinated one that could race it and land a stale write last.
 */
export function useFlowViewStatePersist(options: UseFlowViewStatePersistOptions): void {
  const { stateKey, enabled, activeViewId, working, initialPayloadIsDirty = false, debounceMs = VIEW_STATE_PERSIST_DEBOUNCE_MS } = options;
  const { apiFetch } = useApi();
  const queryClient = useQueryClient();

  const payload = useMemo(
    () => JSON.stringify({ activeViewId, workingState: working } satisfies FlowViewStatePutBody),
    [activeViewId, working],
  );
  // Feeding the debouncer `null` while disabled matters: it stops the
  // PRE-RESTORE seed payload from sitting in the debounce buffer and landing
  // as a write one tick after the gate opens (which would overwrite the rep's
  // restored state with the code-preset default).
  const debounced = useDebouncedValue(enabled ? payload : null, debounceMs);

  const lastPersistedRef = useRef<string | null>(null);
  const initializedRef = useRef(false);
  /** Newest payload observed while `enabled` — the unmount flush's source. */
  const latestEnabledPayloadRef = useRef<string | null>(null);

  const send = useCallback((body: string) => {
    // Recorded as persisted at QUEUE time, not at settle time: the payload is
    // either on the wire or in the pending slot, so re-queueing it would only
    // produce a duplicate write.
    lastPersistedRef.current = body;

    // Keep the cached row in step with what we're writing. The GET is cached
    // indefinitely, so WITHOUT this a Command Center tab switch (unmount →
    // remount inside the same QueryClient) would restore the row as it was
    // when the page loaded and silently throw away the edit the rep just
    // made — Rob's exact complaint, reintroduced one layer down. Written
    // optimistically because the cache mirrors LOCAL state, which is what
    // the rep is looking at whether or not the write lands.
    const writing = safeParseJson(FlowViewStatePutBodySchema, body);
    if (writing.success) {
      queryClient.setQueryData<FlowViewStateResponse>(
        flowViewStateQueryKey(stateKey),
        prev => ({
          activeViewId: writing.data.activeViewId ?? prev?.activeViewId ?? null,
          defaultViewId: prev?.defaultViewId ?? null,
          workingState: writing.data.workingState ?? prev?.workingState ?? null,
        }),
      );
    } else {
      logger.warn({ issues: summarizeIssues(writing.error.issues) }, '[flow-view-state] queued body failed its own schema; cache mirror skipped');
    }

    // Lane busy → overwrite the pending slot. Whatever was queued before is by
    // definition older than this, and the server only cares about the end
    // state, so dropping it is the point rather than a compromise. `lane` is
    // shared by state key (F-C) — "busy" here can mean an in-flight request
    // from a DIFFERENT (e.g. now-unmounted) instance of this hook, not just
    // this one, which is exactly the coordination a remount needs.
    const lane = getPersistLane(stateKey);
    if (lane.inFlight) {
      lane.pending = body;
      return;
    }

    lane.inFlight = true;
    lane.pending = null;
    void (async () => {
      let next: string | null = body;
      while (next !== null) {
        const current: string = next;
        try {
          const res = await backgroundWrite(apiFetch, `/api/flow/view-state/${stateKey}`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: current,
          });
          if (res && !res.ok) throw new Error(`Failed to save view state (${res.status})`);
        } catch (err) {
          // Deliberately terminal FOR THIS BODY: the rep's local state is
          // untouched and still correct on screen; only its durability was
          // lost. Re-sending here would turn a schema-rejected payload (400)
          // into an infinite retry. A newer payload queued behind it is still
          // sent below — a rejected write must not strand later state.
          logger.warn({ err, stateKey }, '[flow-view-state] persist failed');
        }
        // Read from the SHARED lane, not a local capture: whichever instance
        // is still alive may have queued the newest state here while this
        // request was in flight (F-C).
        next = lane.pending;
        lane.pending = null;
      }
      lane.inFlight = false;
    })();
  }, [apiFetch, queryClient, stateKey]);

  // The unmount cleanup below has `[]` deps, so it must not close over a stale
  // `send`; the ref keeps it pointed at the current one.
  const sendRef = useRef(send);
  sendRef.current = send;

  // Lifetime refs get their initial value back at the TOP of the mount effect,
  // not only in cleanup — StrictMode's dev setup→cleanup→setup (and any real
  // remount) otherwise leaves them stuck at the first mount's values.
  // (docs/v4-system/strictmode-resilient-effects.md)
  //
  // The persist LANE (`getPersistLane`, module scope) is deliberately outside
  // this reset entirely — it isn't a ref, isn't per-instance, and describes a
  // request that outlives the component (an unmount flush is exactly that);
  // clearing it here would resurrect the exact cross-instance race F-C fixed.
  // Nothing can be in flight at the StrictMode double-invoke anyway — no time
  // passes between the two setups, so no edit and no flush has happened yet.
  useEffect(() => {
    initializedRef.current = false;
    lastPersistedRef.current = null;
    latestEnabledPayloadRef.current = null;
    return () => {
      const latest = latestEnabledPayloadRef.current;
      // `initializedRef` gates the flush on the store having actually been
      // live: a mount that never opened the gate (the account-scoped roster)
      // has no business writing this user's row, and a pre-restore seed must
      // never be flushed over the rep's stored state.
      if (initializedRef.current && latest !== null && latest !== lastPersistedRef.current) {
        sendRef.current(latest);
      }
      initializedRef.current = false;
      lastPersistedRef.current = null;
      latestEnabledPayloadRef.current = null;
    };
  }, []);

  // Baseline capture + latest-payload tracking, off the LIVE payload (not the
  // debounced one, which still lags a window behind when the gate opens).
  //
  // `initializedRef` flips either way — it gates the unmount flush on the
  // store having been live, which is true the moment the gate opens however
  // the baseline was set. Only the BASELINE differs: A24's dirty case leaves
  // it `null`, so the debounced effect below sees a diff and writes the
  // merged payload exactly once (`send` then records it, so the next
  // identical payload is a no-op).
  useEffect(() => {
    if (!enabled) return;
    if (!initializedRef.current) {
      initializedRef.current = true;
      lastPersistedRef.current = initialPayloadIsDirty ? null : payload;
    }
    latestEnabledPayloadRef.current = payload;
  }, [enabled, payload, initialPayloadIsDirty]);

  useEffect(() => {
    if (!enabled || debounced === null) return;
    if (debounced === lastPersistedRef.current) return;
    send(debounced);
  }, [enabled, debounced, send]);
}
